import {css, StyleSheet} from 'aphrodite';
import {produce} from 'immer';
import React, {Fragment, useState} from 'react';

import {assert} from 'Asserts';
import {Status} from 'graphql-client/__generated/graphqlGlobalTypes';
import {InstrumentServiceDetailsLoader} from 'instruments/connections/ConnectionConstants';
import AutomationFileUtil from 'lab-automation/automation-files/AutomationFileUtil';
import {
  FromServerRunOutputSamplesTable,
  RunOutputSamplesTable,
} from 'lab-automation/output-samples/RunOutputSamplesTableConstants';
import {FromServerResultsTable, ResultsTable} from 'notebook/integrations/results/ResultsTableConstants';
import {NLADVersionedAutomationFiles} from 'runs/AttachmentConstants';
import NLADStructuredTableContainer from 'runs/NLADStructuredTableContainer';
import Col from 'ui-library/bs/Col';
import Row from 'ui-library/bs/Row';
import FieldLabel from 'ui-library/inputs/FieldLabel';

import {AutomationErrorFileGraphQL} from '../../../graphql-fragments/lab-automation/__generated/AutomationErrorFileGraphQL';
import {AutomationInputFileGraphQL} from '../../../graphql-fragments/lab-automation/__generated/AutomationInputFileGraphQL';
import {AutomationOutputFileConfigGraphQL} from '../../../graphql-fragments/lab-automation/__generated/AutomationOutputFileConfigGraphQL';
import {AutomationOutputFileGraphQL} from '../../../graphql-fragments/lab-automation/__generated/AutomationOutputFileGraphQL';
import ProcessAllAutomationOutputFilesButton from './ProcessAllAutomationOutputFilesButton';
import RunAutomationErrorFileViewer from './RunAutomationErrorFileViewer';
import RunAutomationInputFileViewer from './RunAutomationInputFileViewer';
import RunAutomationOutputFileUploader from './RunAutomationOutputFileUploader';
import RunAutomationOutputFileViewer from './RunAutomationOutputFileViewer';

interface RunAutomationFileViewerProps {
  runId: string | null;
  automationFiles: NLADVersionedAutomationFiles;
  isConnectionRun: boolean;
  connectionId: string | null;
  instrumentServiceDetails: InstrumentServiceDetailsLoader;
  locked: boolean;
  handleRefreshVersionedData: () => Promise<void>;
  handleUpdateAutomationFiles: (
    files: Partial<NLADVersionedAutomationFiles>,
    structuredTables?: Array<FromServerRunOutputSamplesTable | FromServerResultsTable>
  ) => void;
  getIdFromCid?: (cid: string) => string | number | undefined;
  versionedStructuredTables?: Array<RunOutputSamplesTable | ResultsTable>;
  fileId?: string;
  viewer?: boolean;
  refreshCounter?: number;
}

const RunAutomationFileViewer: React.FunctionComponent<RunAutomationFileViewerProps> = (
  props: RunAutomationFileViewerProps
): JSX.Element | null => {
  const [isArchiveModalOpen, setIsArchiveModalOpen] = useState(false);
  const [archiveAutomationOutputFileName, setArchiveAutomationOutputFileName] = useState('');
  const [archiveAutomationOutputFileId, setArchiveAutomationOutputFileId] = useState('');

  const closeArchiveModal = (): void => {
    setArchiveAutomationOutputFileId('');
    setArchiveAutomationOutputFileName('');
    setIsArchiveModalOpen(false);
  };

  const openArchiveModal = (automationOutputFileId: string, automationOutputFileName: string): void => {
    setArchiveAutomationOutputFileId(automationOutputFileId);
    setArchiveAutomationOutputFileName(automationOutputFileName);
    setIsArchiveModalOpen(true);
  };

  const automationInputFilesByName: Record<string, AutomationInputFileGraphQL> = {};
  const automationInputFiles = props.automationFiles.automationInputFiles || [];
  for (const automationInputFile of automationInputFiles) {
    automationInputFilesByName[automationInputFile.automationInputFileConfig.name] = automationInputFile;
  }
  const [automationOutputFilesByName, automationOutputFiles] =
    AutomationFileUtil.generateAutomationOutputFilesByName(props.automationFiles.automationOutputFiles || []);
  const automationErrorFilesByName: {[name: string]: AutomationErrorFileGraphQL} = {};
  const automationErrorFiles = props.automationFiles.automationErrorFiles || [];
  for (const automationErrorFile of automationErrorFiles) {
    automationErrorFilesByName[automationErrorFile.automationOutputFileConfig.name] = automationErrorFile;
  }

  const automationOutputFileIdsToStructuredTable =
    AutomationFileUtil.generateAutomationOutputFileIdsToStructuredTable(props.versionedStructuredTables);

  const configNameToLastSucceededAutomationOutputFiles =
    AutomationFileUtil.generateConfigNameToLastSucceededAutomationOutputFiles(automationOutputFilesByName);

  function updateAutomationInputFile(
    automationInputFile: AutomationInputFileGraphQL,
    automationOutputFile: AutomationOutputFileGraphQL | null
  ): void {
    const newAutomationInputFiles = [...automationInputFiles];
    const configName = automationInputFile.automationInputFileConfig.name;
    const newIndex = automationInputFiles.findIndex((f) => f.automationInputFileConfig.name === configName);
    assert(newIndex > -1, `No automation input file found with name ${configName}`);
    newAutomationInputFiles[newIndex] = automationInputFile;
    if (automationOutputFile == null) {
      props.handleUpdateAutomationFiles({
        automationInputFiles: newAutomationInputFiles,
      });
    } else {
      const newAutomationOutputFiles = produce(automationOutputFiles, (draftAutomationOutputFiles) => {
        const idx = automationOutputFiles.findIndex((f) => f.id === automationOutputFile.id);
        if (idx > -1) {
          draftAutomationOutputFiles[idx] = automationOutputFile;
        } else {
          draftAutomationOutputFiles.push(automationOutputFile);
        }
      });
      props.handleUpdateAutomationFiles({
        automationInputFiles: newAutomationInputFiles,
        automationOutputFiles: newAutomationOutputFiles,
      });
    }
  }

  function updateAutomationOutputFiles(
    updatedAutomationOutputFiles: Array<AutomationOutputFileGraphQL>,
    structuredTables?: Array<FromServerRunOutputSamplesTable | FromServerResultsTable>
  ): void {
    const newAutomationOutputFiles = produce(automationOutputFiles, (draftAutomationOutputFiles) => {
      for (const automationOutputFile of updatedAutomationOutputFiles) {
        const idx = automationOutputFiles.findIndex((f) => f.id === automationOutputFile.id);
        if (idx > -1) {
          draftAutomationOutputFiles[idx] = automationOutputFile;
        } else {
          draftAutomationOutputFiles.push(automationOutputFile);
        }
      }
    });
    props.handleUpdateAutomationFiles({automationOutputFiles: newAutomationOutputFiles}, structuredTables);
  }

  function updateAutomationErrorFile(automationErrorFile: AutomationErrorFileGraphQL): void {
    const newAutomationErrorFiles = [...automationErrorFiles];
    const configName = automationErrorFile.automationOutputFileConfig.name;
    const newIndex = automationErrorFiles.findIndex((f) => f.automationOutputFileConfig.name === configName);
    assert(newIndex > -1, `No automation error file found with name ${configName}`);
    newAutomationErrorFiles[newIndex] = automationErrorFile;
    props.handleUpdateAutomationFiles({
      automationErrorFiles: newAutomationErrorFiles,
    });
  }

  // eslint-disable-next-line no-restricted-syntax
  const allNames = [
    ...new Set([
      ...Object.keys(automationInputFilesByName),
      ...Object.keys(automationOutputFilesByName),
      ...Object.keys(automationErrorFilesByName),
    ]),
  ].sort();
  if (!allNames.length) {
    return null;
  }

  // To warn on duplicate uploads
  const previousFileNames: Set<string> = new Set(
    [
      ...automationOutputFiles.map((file) => file.blob?.name),
      ...automationErrorFiles.map((file) => file.blob?.name),
    ].filter((name) => Boolean(name)) as Array<string> // Typescript isn't smart enough to filter undefined
  );

  const runAutomationInputFileViewerItem = (
    props: RunAutomationFileViewerProps,
    name: string
  ): JSX.Element | null => {
    const automationInputFile = automationInputFilesByName[name];
    if (!automationInputFile) {
      return null;
    }

    const automationInputFileConfig = automationInputFile.automationInputFileConfig;
    const isHiddenUnlessErrored = automationInputFileConfig.isHidden;
    const processingFailures = automationInputFile.status === Status.failed;
    if (isHiddenUnlessErrored && !processingFailures) {
      return null;
    }

    return (
      <RunAutomationInputFileViewer
        runId={props.runId}
        automationInputFile={automationInputFilesByName[name]}
        locked={props.locked}
        isConnectionRun={props.isConnectionRun}
        handleRefreshVersionedData={props.handleRefreshVersionedData}
        updateAutomationInputFile={updateAutomationInputFile}
        hasOutputFileConfig={automationOutputFilesByName[name] != null}
        viewer={props.viewer}
        refreshCounter={props.refreshCounter}
        connectionId={props.connectionId}
        instrumentServiceDetails={props.instrumentServiceDetails.data}
      />
    );
  };

  const runAutomationOutputFileViewerItem = (
    props: RunAutomationFileViewerProps,
    name: string,
    index: number
  ): JSX.Element | null => {
    const automationOutputFiles = automationOutputFilesByName[name];

    let filteredAutomationOutputFiles: Array<AutomationOutputFileGraphQL> | null =
      AutomationFileUtil.filterAutomationOutputFiles(automationOutputFiles);
    if (filteredAutomationOutputFiles == null) {
      filteredAutomationOutputFiles = [];
    }

    return (
      <RunAutomationOutputFileViewer
        key={index}
        automationOutputFiles={filteredAutomationOutputFiles}
        renderTable={(automationOutputFile) => {
          const structuredTable = automationOutputFileIdsToStructuredTable[automationOutputFile.id];
          if (structuredTable == null || props.getIdFromCid == null) {
            return null;
          }
          return (
            <div className={css(styles.processedAutomationFile)}>
              <NLADStructuredTableContainer
                versionedStructuredTable={structuredTable}
                getIdFromCid={props.getIdFromCid}
                automationOutputFileArchiveRecord={automationOutputFile.archiveRecord}
                viewer={props.viewer}
              />
            </div>
          );
        }}
        runId={props.runId}
        locked={props.locked}
        handleRefreshVersionedData={props.handleRefreshVersionedData}
        updateAutomationOutputFiles={updateAutomationOutputFiles}
        openArchiveModal={openArchiveModal}
        closeArchiveModal={() => {
          closeArchiveModal();
        }}
        archiveAutomationOutputFileId={archiveAutomationOutputFileId}
        archiveAutomationOutputFileName={archiveAutomationOutputFileName}
        isArchiveModalOpen={isArchiveModalOpen}
        viewer={props.viewer}
        configName={name}
        previousFileNames={previousFileNames}
        hasSucceededAutomationOutputFiles={configNameToLastSucceededAutomationOutputFiles[name] != null}
        refreshCounter={props.refreshCounter}
      />
    );
  };

  const runAutomationOutputFileUploaderItem = (
    props: RunAutomationFileViewerProps,
    name: string,
    shouldHide: Boolean,
    automationOutputFileConfig: AutomationOutputFileConfigGraphQL | null
  ): JSX.Element | null => {
    const automationOutputFiles = automationOutputFilesByName[name];
    return shouldHide || !automationOutputFiles ? null : (
      <>
        <RunAutomationOutputFileUploader
          locked={props.locked}
          runId={props.runId}
          configName={name}
          automationOutputFileConfig={automationOutputFileConfig}
          isConnectionRun={props.isConnectionRun}
          connectionId={props.connectionId}
          instrumentServiceDetails={props.instrumentServiceDetails}
          updateAutomationOutputFiles={updateAutomationOutputFiles}
          previousFileNames={previousFileNames}
          disabled={false}
        />
        <ProcessAllAutomationOutputFilesButton
          locked={props.locked}
          runId={props.runId}
          updateAutomationOutputFiles={updateAutomationOutputFiles}
          fileId={props.fileId}
          automationOutputFiles={automationOutputFiles}
          handleRefreshVersionedData={props.handleRefreshVersionedData}
        />
      </>
    );
  };

  const runAutomationErrorFileViewerItem = (
    props: RunAutomationFileViewerProps,
    name: string,
    shouldHide: Boolean
  ): JSX.Element | null => {
    const automationErrorFile = automationErrorFilesByName[name];

    return shouldHide ? null : (
      <RunAutomationErrorFileViewer
        runId={props.runId}
        automationErrorFile={automationErrorFile}
        locked={props.locked}
        updateAutomationErrorFile={updateAutomationErrorFile}
      />
    );
  };

  interface SectionData {
    runAutomationInputFileViewerItem: JSX.Element | null;
    runAutomationOutputFileViewerItem: JSX.Element | null;
    runAutomationOutputFileUploaderItem: JSX.Element | null;
    runAutomationErrorFileViewerItem: JSX.Element | null;
  }

  const aggregateRunAutomationSectionItems = (
    props: RunAutomationFileViewerProps
  ): Map<string, null | SectionData> | null => {
    const fileViewerItemsMap: Map<string, null | SectionData> = new Map();
    let noIndicesShown = true;
    allNames.forEach((name: string, index: number) => {
      const inputItem = runAutomationInputFileViewerItem(props, name);

      const automationOutputFiles = automationOutputFilesByName[name];
      const automationOutputFileConfig = automationOutputFiles
        ? automationOutputFiles[0].automationOutputFileConfig
        : null;
      const outputFilesHidden =
        automationOutputFiles && automationOutputFileConfig?.isHidden !== undefined
          ? automationOutputFileConfig.isHidden
          : false;
      const outputFilesWithBlobs = automationOutputFiles
        ? automationOutputFiles.filter((automationOutputFile) => automationOutputFile.blob != null).length > 0
        : false;
      const outputItem = runAutomationOutputFileViewerItem(props, name, index);
      const uploadItem = runAutomationOutputFileUploaderItem(
        props,
        name,
        outputFilesHidden,
        automationOutputFileConfig
      );
      const errorItem = runAutomationErrorFileViewerItem(props, name, outputFilesHidden);

      const inputItemExists = Boolean(automationInputFilesByName[name]);
      const outputItemExists = Boolean(automationOutputFilesByName[name]);
      const inputItemIsHidden = inputItemExists && !inputItem;

      // Shorthand for everything being hidden
      // We only return a true null if the item is hidden, otherwise we get back the relevant
      // react component with no data inside of it

      const shouldHideAllForIndex =
        ((!outputFilesWithBlobs && outputFilesHidden) || !outputItemExists) &&
        (inputItemIsHidden || !inputItemExists);

      if (shouldHideAllForIndex) {
        fileViewerItemsMap.set(name, null);
      } else {
        fileViewerItemsMap.set(name, {
          runAutomationInputFileViewerItem: inputItem,
          runAutomationOutputFileViewerItem: outputItem,
          runAutomationOutputFileUploaderItem: uploadItem,
          runAutomationErrorFileViewerItem: errorItem,
        });
        noIndicesShown = false;
      }
    });
    if (noIndicesShown) {
      return null;
    }
    return fileViewerItemsMap;
  };

  const resultsAttachmentSection = (
    items: Map<string, null | SectionData> | null,
    name: string,
    i: number
  ): JSX.Element | null => {
    if (items == null) {
      return null;
    }
    const sectionData = items.get(name);
    if (sectionData == null) {
      return null;
    }
    if (
      sectionData.runAutomationErrorFileViewerItem == null &&
      sectionData.runAutomationInputFileViewerItem == null &&
      sectionData.runAutomationOutputFileUploaderItem == null
    ) {
      return null;
    }
    return (
      <Fragment key={i}>
        {i % 3 === 0 && <Col className="clearfix" key={`clearfix-${i}`} />}
        <Col className="resultsAttachment-assayRun-fieldSection" key={name} sm={4}>
          <FieldLabel className="resultsAttachment-assayRun-fieldHeader u-tiny-spacing-bottom" label={name} />
          <div className="resultsAttachment-assayRun-automationFileViewer">
            {sectionData.runAutomationInputFileViewerItem}
            {sectionData.runAutomationOutputFileUploaderItem}
            {sectionData.runAutomationErrorFileViewerItem}
          </div>
        </Col>
      </Fragment>
    );
  };

  const runAutomationSectionItems = aggregateRunAutomationSectionItems(props);

  return runAutomationSectionItems == null ? null : (
    <>
      <hr />
      <Row bufferRowReset={true}>
        {allNames.map((name: string, i: number) =>
          resultsAttachmentSection(runAutomationSectionItems, name, i)
        )}
      </Row>
      <React.Fragment key="run-automation-processed-file-viewer">
        {allNames.map((name: string, index: number) => {
          const sectionData = runAutomationSectionItems.get(name);
          return sectionData == null ? null : (
            <React.Fragment key={index}>{sectionData.runAutomationOutputFileViewerItem}</React.Fragment>
          );
        })}
      </React.Fragment>
    </>
  );
};

export default RunAutomationFileViewer;

const styles = StyleSheet.create({
  processedAutomationFile: {
    marginTop: '-10px',
  },
});
